if (!customElements.get('volume-confirm-modal')) {
  /* Products can require a confirmation before they reach the cart for two
     independent reasons, and a product can hit both at once:

       Fulfillment:InternationalMarketplace -> stored in France, 5 month dispatch
       Volume:<n> with n < 750              -> a small bottle

     Both are read off the product's tags. The modal stacks the two messages in
     that order when both apply, and shows nothing at all when neither does. */
  var VC_INTL_TAG = 'fulfillment:internationalmarketplace';
  var VC_MAX_VOLUME = 750;

  // Tags arrive with inconsistent spacing ("Volume: 375", "Fulfillment: International Marketplace").
  function vcNormalizeTag(tag) {
    return String(tag).toLowerCase().replace(/\s+/g, '');
  }

  // Reads the two confirmation flags out of a product's tag list.
  window.vcFlagsFromTags = function (tags) {
    var flags = { volume: null, international: false };
    (tags || []).forEach(function (tag) {
      var normalized = vcNormalizeTag(tag);
      if (normalized === VC_INTL_TAG) {
        flags.international = true;
        return;
      }
      if (flags.volume === null) {
        var match = normalized.match(/^volume:(\d+)/);
        if (match) flags.volume = parseInt(match[1], 10);
      }
    });
    return flags;
  };

  // The single place that decides whether a product needs the modal at all.
  window.vcNeedsConfirm = function (flags) {
    if (!flags) return false;
    return Boolean(flags.international) || (Number.isFinite(flags.volume) && flags.volume < VC_MAX_VOLUME);
  };

  customElements.define('volume-confirm-modal', class VolumeConfirmModal extends HTMLElement {
    constructor() {
      super();

      this.dialog = this.querySelector('.volume-confirm-modal__dialog');
      this.volumeEl = this.querySelector('[data-vc-volume]');
      this.volumeBlock = this.querySelector('[data-vc-volume-block]');
      this.intlBlock = this.querySelector('[data-vc-intl]');
      this.confirmBtn = this.querySelector('[data-vc-confirm]');
      this.cancelEls = this.querySelectorAll('[data-vc-cancel]');
      this.boundKey = this.onKey.bind(this);

      this.confirmBtn.addEventListener('click', () => this.resolve(true));
      this.cancelEls.forEach((el) => el.addEventListener('click', () => this.resolve(false)));
    }

    confirm({ volume, international } = {}) {
      if (this._pending) return this._pending;

      const showVolume = Number.isFinite(parseInt(volume, 10));
      if (this.volumeEl) this.volumeEl.textContent = showVolume ? parseInt(volume, 10) : '';
      if (this.volumeBlock) this.volumeBlock.toggleAttribute('hidden', !showVolume);
      if (this.intlBlock) this.intlBlock.toggleAttribute('hidden', !international);

      this._pending = new Promise((resolve) => {
        this._resolve = resolve;
        this.previouslyFocused = document.activeElement;
        this.removeAttribute('hidden');
        document.addEventListener('keydown', this.boundKey);
        if (typeof trapFocus === 'function') {
          trapFocus(this.dialog, this.confirmBtn);
        } else {
          setTimeout(() => this.confirmBtn && this.confirmBtn.focus(), 10);
        }
      });
      return this._pending;
    }

    resolve(value) {
      if (!this._resolve) return;
      const fn = this._resolve;
      this._resolve = null;
      this._pending = null;
      this.setAttribute('hidden', '');
      document.removeEventListener('keydown', this.boundKey);
      if (typeof removeTrapFocus === 'function') {
        removeTrapFocus(this.previouslyFocused);
      } else if (this.previouslyFocused && this.previouslyFocused.focus) {
        try { this.previouslyFocused.focus(); } catch (e) { /* element may have been removed */ }
      }
      fn(value);
    }

    onKey(e) {
      if (e.key === 'Escape') this.resolve(false);
    }
  });

  window.requestVolumeConfirm = function ({ volume, international } = {}) {
    const el = document.querySelector('volume-confirm-modal');
    if (el && typeof el.confirm === 'function') return el.confirm({ volume, international });

    // Native fallback if the snippet is missing from the layout.
    const lines = [];
    if (international) {
      lines.push(
        "This product is currently located in our Partner's Storage & Cellaring Facility in France. " +
        'Expected dispatch date from our Sydney warehouse is in 5 months.'
      );
    }
    if (Number.isFinite(parseInt(volume, 10))) {
      lines.push(`You are adding a ${parseInt(volume, 10)}ml bottle.`);
    }
    lines.push('Would you like to proceed?');
    return Promise.resolve(window.confirm(lines.join('\n\n')));
  };

  // Boost AI Search & Filter (CLP/PLP) integration. Their product card uses an
  // inline onclick to call window.globalQuickAdd, bypassing our <product-form>.
  // We delegate clicks at the document capture phase, gate by the product's
  // tags (fetched lazily), then re-fire the click to let Boost run.
  (function gateBoostQuickAdd() {
    const cache = new Map();

    // A confirmed product can reach /cart/add through more than one click: for a
    // multi-variant product Boost re-opens its own variant picker, whose add
    // button comes back through this same handler. Remember the handle briefly
    // so the customer is asked once, not once per step.
    const recentlyConfirmed = new Map();
    const CONFIRM_GRACE_MS = 8000;

    function wasJustConfirmed(handle) {
      const expiry = recentlyConfirmed.get(handle);
      if (!expiry) return false;
      if (expiry < Date.now()) {
        recentlyConfirmed.delete(handle);
        return false;
      }
      return true;
    }

    async function fetchFlags(handle) {
      if (cache.has(handle)) return cache.get(handle);
      try {
        const data = await fetch('/products/' + encodeURIComponent(handle) + '.js').then((r) => r.json());
        const flags = window.vcFlagsFromTags((data && data.tags) || []);
        cache.set(handle, flags);
        return flags;
      } catch (e) {
        return null;
      }
    }

    document.addEventListener('click', async function (event) {
      const btn = event.target.closest && event.target.closest('button[onclick*="globalQuickAdd"]');
      if (!btn) return;
      if (btn.__vcConfirmed) {
        btn.__vcConfirmed = false;
        return;
      }

      const ancestorAnchor = btn.closest('a[href*="/products/"]');
      const card = btn.closest('.boost-sd__product-item, .boost-sd__product-list-item, .product-card, [data-product], article, li');
      const link = ancestorAnchor || (card && card.querySelector('a[href*="/products/"]'));
      if (!link) return;
      const m = (link.getAttribute('href') || '').match(/\/products\/([^/?#]+)/);
      if (!m) return;
      const handle = m[1];
      if (wasJustConfirmed(handle)) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      const flags = await fetchFlags(handle);
      if (window.vcNeedsConfirm(flags)) {
        btn.setAttribute('aria-disabled', 'true');
        try {
          const proceed = await window.requestVolumeConfirm({
            volume: Number.isFinite(flags.volume) && flags.volume < 750 ? flags.volume : null,
            international: flags.international,
          });
          if (!proceed) return;
        } finally {
          btn.removeAttribute('aria-disabled');
        }
        recentlyConfirmed.set(handle, Date.now() + CONFIRM_GRACE_MS);
      }

      btn.__vcConfirmed = true;
      btn.click();
    }, true);
  })();
}
